// Mirrors reporting-hub's PreflightPage: a 4-tab wizard (Select Data ->
// Validation -> Calculation -> Reporting), gated on the reporting's real
// server-tracked `stage` (GET .../status) rather than reporting-hub's own
// separate client-only "unlocked" booleans - same gating outcome, simpler
// source of truth since the server already knows the stage authoritatively.

import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import axiosWithAuth from '../../../helpers/AxiosHelper';
import { Button } from 'primereact/button';
import { TabView, TabPanel } from 'primereact/tabview';
import SelectDataTab from '../wizard/SelectDataTab';
import ValidationTab from '../wizard/ValidationTab';
import CalculationTab from '../wizard/CalculationTab';
import ReportingTab from '../wizard/ReportingTab';
import LoanTapeViewerDialog from '../loantape/LoanTapeViewerDialog';
import JobProgress from '../JobProgress';
import PageHeader from '../../common/PageHeader';
import { ACTION_LABELS, WizardJobProvider, useJobRunner } from '../jobs';
import errorMessage from '../../../helpers/errorMessage';

const STAGE_ORDER = { created: 0, preflight: 1, calculated: 2, reported: 3 };

// Step-indicator tab header (presentation only - which tab is enabled is
// still decided by the *Enabled flags below): number badge, or a check once
// the reporting's stage has passed that step, or a lock while disabled.
const StepHeader = ({ index, label, hint, done, locked }) => (
  <span className={`cr-wstep${done ? ' is-done' : ''}${locked ? ' is-locked' : ''}`}>
    <span className="cr-wstep-badge" aria-hidden="true">
      {locked ? <i className="pi pi-lock" /> : done ? <i className="pi pi-check" /> : index}
    </span>
    <span className="cr-wstep-text">
      <span className="cr-wstep-label">{label}</span>
      <span className="cr-wstep-hint">{hint}</span>
    </span>
  </span>
);

const PreflightWizardPage = ({ reportingId: initialReportingId, onBack, setLoading }) => {
  const [reportingId, setReportingId] = useState(initialReportingId);
  const [status, setStatus] = useState(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [loanTapeViewerOpen, setLoanTapeViewerOpen] = useState(false);
  const [resumedJobError, setResumedJobError] = useState(null);
  const jumpToLoanTapeRowRef = useRef(null);
  const reportingIdRef = useRef(initialReportingId);
  const attachedJobIdRef = useRef(null);

  // All wizard jobs run through this one runner so polling (and the progress
  // banner) survives tab switches - TabView unmounts inactive tabs.
  const runner = useJobRunner();
  const { attach: attachJob, isBusy: isJobBusy, run: runnerRun, job: activeJob } = runner;

  const openLoanTapeViewer = (identifier) => {
    if (identifier && jumpToLoanTapeRowRef.current) jumpToLoanTapeRowRef.current(identifier);
    setLoanTapeViewerOpen(true);
  };

  const fetchStatus = useCallback(async () => {
    const id = reportingIdRef.current;
    if (!id) {
      setStatus(null);
      return null;
    }
    try {
      const res = await axiosWithAuth.get(`/compartment-report/reportings/${id}/status`);
      if (reportingIdRef.current === id) setStatus(res.data);
      return res.data;
    } catch (err) {
      console.error('[PreflightWizardPage] status fetch error:', errorMessage(err));
      return null;
    }
  }, []);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus, reportingId]);

  // A job queued/running on the server that this page isn't already polling
  // (e.g. started before a reload, or from another browser tab): show its
  // progress and refresh status once it finishes.
  useEffect(() => {
    const job = status && status.active_job;
    if (!job || isJobBusy() || attachedJobIdRef.current === job.job_id) return;
    attachedJobIdRef.current = job.job_id;
    setResumedJobError(null);
    attachJob(job)
      .catch((err) => {
        if (!err.cancelled) setResumedJobError(`${ACTION_LABELS[job.action] || job.action} failed: ${err.detail}`);
      })
      .finally(() => fetchStatus());
  }, [status, attachJob, isJobBusy, fetchStatus]);

  const runWizardJob = useCallback(
    (id, action, params) => {
      setResumedJobError(null);
      return runnerRun(id, action, params).finally(() => {
        if (reportingIdRef.current === id) fetchStatus();
      });
    },
    [runnerRun, fetchStatus]
  );

  const jobContext = useMemo(() => ({ runJob: runWizardJob, activeJob }), [runWizardJob, activeJob]);

  const handleReportingCreated = async (id) => {
    reportingIdRef.current = id;
    setReportingId(id);
    await fetchStatus();
  };

  const handleValidated = async () => {
    await fetchStatus();
    setActiveIndex(1);
  };

  const stageIndex = status ? STAGE_ORDER[status.stage] ?? 0 : 0;
  const validationEnabled = !!reportingId && stageIndex >= STAGE_ORDER.preflight;
  const calculationEnabled = validationEnabled;
  const reportingEnabled = !!reportingId && stageIndex >= STAGE_ORDER.reported;
  const validationStale = !!(status && status.validation && status.validation.stale);

  return (
    <WizardJobProvider value={jobContext}>
    <div className="content-container">
      <PageHeader
        title={`Compartment Report${reportingId ? ` - ${reportingId}` : ' - New Reporting'}`}
        subtitle={
          status && status.stage ? (
            <span className="cr-header-meta">
              Stage <span className="ex-status ex-status-info cr-cap">{status.stage}</span>
            </span>
          ) : (
            'Select the input data to start this reporting.'
          )
        }
        icon="pi-briefcase"
        actions={<Button label="Back to History" icon="pi pi-arrow-left" className="p-button-secondary p-button-outlined" onClick={onBack} />}
      />

      {activeJob && (
        <div className="cr-sticky-progress">
          <JobProgress job={activeJob} />
        </div>
      )}
      {resumedJobError && (
        <div className="ex-inline-error cr-dismissible-error mb-3">
          <span>{resumedJobError}</span>{' '}
          <Button icon="pi pi-times" className="p-button-text p-button-rounded p-button-sm" onClick={() => setResumedJobError(null)} aria-label="Dismiss" />
        </div>
      )}

      <TabView className="cr-wizard" activeIndex={activeIndex} onTabChange={(e) => setActiveIndex(e.index)}>
        <TabPanel header={<StepHeader index={1} label="Select Data" hint="Loan tape & Originator files" done={validationEnabled} />}>
          <SelectDataTab
            reportingId={reportingId}
            status={status}
            onReportingCreated={handleReportingCreated}
            onValidated={handleValidated}
            onStatusChanged={fetchStatus}
            setLoading={setLoading}
          />
        </TabPanel>
        <TabPanel
          header={
            <StepHeader
              index={2}
              label="Validation"
              hint={validationStale ? 'Inputs changed - re-validate' : 'Review checks & exclusions'}
              done={validationEnabled && !validationStale}
              locked={!validationEnabled}
            />
          }
          disabled={!validationEnabled}
        >
          <ValidationTab reportingId={reportingId} setLoading={setLoading} onViewLoanTape={openLoanTapeViewer} />
        </TabPanel>
        <TabPanel
          header={
            <StepHeader
              index={3}
              label="Calculation"
              hint="Calculate & generate"
              done={stageIndex >= STAGE_ORDER.calculated}
              locked={!calculationEnabled}
            />
          }
          disabled={!calculationEnabled}
        >
          <CalculationTab
            reportingId={reportingId}
            status={status}
            onStageChanged={fetchStatus}
            setLoading={setLoading}
          />
        </TabPanel>
        <TabPanel
          header={<StepHeader index={4} label="Reporting" hint="PDF, projection & IC" done={reportingEnabled} locked={!reportingEnabled} />}
          disabled={!reportingEnabled}
        >
          <ReportingTab reportingId={reportingId} status={status} setLoading={setLoading} />
        </TabPanel>
      </TabView>

      {reportingId && (
        <LoanTapeViewerDialog
          reportingId={reportingId}
          open={loanTapeViewerOpen}
          onOpenChange={setLoanTapeViewerOpen}
          snapshotVersion={status && status.validation ? status.validation.validated_at : null}
          onRegisterJump={(jump) => {
            jumpToLoanTapeRowRef.current = jump;
          }}
        />
      )}
    </div>
    </WizardJobProvider>
  );
};

export default PreflightWizardPage;
